import { EmailIcon } from "@chakra-ui/icons";
import { useToast } from "@chakra-ui/react";
import { useContext, useState } from "react";
import { useIntl } from "react-intl";

import {
  InvitationListOrderByEnum,
  InvitationListRequest,
  InvitationRead,
  InvitationStatus,
} from "@nens/rana/api-v1";
import { RevokeIcon } from "@nens/ranalib";

import { useRanaSearchState } from "@/hooks/router/rana-search-state";
import { useRanaTableData } from "@/hooks/router/use-rana-table-data";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { useTenantActionAllowed } from "@/hooks/use-action-allowed-in-tenant";
import { useInvitedUserListQuery } from "@/queries/query-user-invited";
import { ProjectContext } from "@/stores/project-provider";

import { useResendInvite } from "../api/resend-user-invite";

type InviteModalType = { type: "revoke"; invitations: InvitationRead[] } | null;

type InvitationStatusWithExpired = InvitationStatus | "expired";

const getInvitationFilters = (
  status: InvitationStatusWithExpired | null
): Partial<InvitationListRequest> => {
  if (status === "expired") {
    return { status: ["pending"], isExpired: true };
  }

  if (status === "pending") {
    return { status: ["pending"], isExpired: false };
  }

  if (status) {
    return { status: [status] };
  }

  return {};
};

const statusses: InvitationStatusWithExpired[] = ["pending", "expired", "accepted", "revoked"];

export const useUserInviteList = () => {
  const toast = useToast();
  const intl = useIntl();
  const project = useContext(ProjectContext);
  const inProject = !!project.id;

  const [modal, setModal] = useState<InviteModalType>(null);

  const [_selectedStatus, setSelectedStatus] =
    useRanaSearchState<InvitationStatusWithExpired | null>("invStatus", "pending", [
      "string",
      "null",
    ]);
  const selectedStatus = statusses.find((status) => status === _selectedStatus) || null;

  const { canManageTenant } = useTenantActionAllowed();
  const { canManageProject } = useProjectActionAllowed();

  const canManage = inProject ? canManageProject : canManageTenant;

  const { page, offset, setPageSize, limit, setPage, orderings, orderingString, setOrdering } =
    useRanaTableData({
      keyAddon: "userInvite",
      initialOrderings: { created_at: "za" },
      initialPageSize: 5,
    });

  const { data, isLoading: isSkeleton } = useInvitedUserListQuery({
    offset,
    limit,
    orderBy: (orderingString as InvitationListOrderByEnum) || null,
    project: inProject ? project.id : null,
    tenantOnly: !inProject,
    // Filter by status with special handling for "expired" and "pending".
    ...getInvitationFilters(selectedStatus as InvitationStatusWithExpired | null),
  });
  const userInvitesList = data?.items ?? [];

  const { mutate: resendInvite } = useResendInvite();

  const getTableActions = () => {
    return [
      {
        label: intl.formatMessage({
          id: "action.revoke",
          defaultMessage: "Revoke",
        }),
        action: (rowIds: string[]) => {
          const filteredInvitations = userInvitesList.filter(
            ({ id, status }) => rowIds.includes(id) && status === "pending"
          );

          if (!filteredInvitations.length) {
            return toast({
              status: "error",
              title: intl.formatMessage({
                id: "error",
                defaultMessage: "Error!",
              }),
              description: intl.formatMessage({
                id: "invite.non_revokable",
                defaultMessage: "None of these invites are able to be revoked.",
              }),
            });
          }

          setModal({
            type: "revoke",
            invitations: filteredInvitations,
          });
        },

        icon: <RevokeIcon />,
      },
    ];
  };

  const getActions = (invite: InvitationRead) => {
    const { id: invitationId, status } = invite;
    if (status === "accepted" || status === "revoked") return undefined;

    return [
      {
        icon: <EmailIcon />,
        label: intl.formatMessage({
          id: "action.resend_email",
          defaultMessage: "Resend email",
        }),
        action: () => resendInvite({ invitationId }),
      },
      {
        icon: <RevokeIcon />,
        label: intl.formatMessage({
          id: "action.revoke",
          defaultMessage: "Revoke",
        }),
        action: () => setModal({ type: "revoke", invitations: [invite] }),
      },
    ];
  };

  return {
    page,
    setPageSize,
    setPage,
    orderings,
    setOrdering,
    userInvitesList,
    isSkeleton,
    inProject,
    numPages: Math.ceil((data?.total ?? 0) / limit),
    limit,
    getTableActions,
    getActions,
    canManage,
    statusses,
    modal,
    onClose: () => setModal(null),
    dropdownProps: {
      selected: selectedStatus,
      setSelected: (id: InvitationStatusWithExpired | null) => {
        setPage(1);
        setSelectedStatus(id);
      },
    },
  };
};
